import {
  either as E,
  function as F,
  option as O,
  reader as R,
  readonlyArray as RA,
} from 'fp-ts';
import { Do } from 'fp-ts-contrib';
import * as P from 'parser-ts';
import { run } from 'parser-ts/es6/code-frame';

const sum = F.flow(RA.reduce(0, (acc: number, x: number) => acc + x));
const integersParser = F.pipe(
  P.parser.many1(P.char.digit),
  P.parser.map(x => parseInt(x.join(''), 10)),
);

const parseNumericalMinutes =
  // Parse numerical minutes to seconds.
  // .1 -> 360
  Do.Do(P.parser.parser)
    .do(P.char.char('.'))
    .bind(
      'minutes',
      F.pipe(
        P.parser.many1(P.char.digit),
        P.parser.map(x => parseFloat('0.' + x.join('')) * 60 * 60),
      ),
    )
    .return(({ minutes }) => minutes);

const parseNumericalHours =
  // Parse numerical hours to seconds.
  // 1 -> 3600
  // 1.1 -> 3960
  // 0.1 -> 360
  Do.Do(P.parser.parser)
    .bind(
      'hours',
      F.pipe(
        integersParser,
        P.parser.map(x => x * 60 * 60),
      ),
    )
    .bind(
      'minutes',
      F.pipe(
        P.parser.optional(parseNumericalMinutes),
        P.parser.map(O.fold(() => 0, F.identity)),
      ),
    )
    .return(({ hours, minutes }) => minutes + hours);

const parseSuffixedHour =
  // Parse suffixed hour to seconds.
  // 1h -> 3600
  // 1.1h -> 3960
  // 0.1h -> 360
  Do.Do(P.parser.parser)
    .bind('hours', parseNumericalHours)
    .do(P.char.char('h'))
    .return(({ hours }) => hours);

const parseSuffixedMinute =
  // Parse suffixed minutes to seconds.
  // 1m -> 60
  // 1.5m -> 90
  // 0.5m -> 30
  Do.Do(P.parser.parser)
    .bind(
      'minutes',
      F.pipe(
        parseNumericalHours,
        P.parser.map(x => x / 60),
      ),
    )
    .do(P.char.char('m'))
    .return(({ minutes }) => minutes);

const parseTimePart = F.pipe(
  P.parser.many1(P.char.digit),
  P.parser.map(x => parseFloat('0.' + x.join('').padStart(2, '0')) * 100),
);

const parseTimeFromString =
  // Parse time from string to seconds.
  // 1: -> 3600
  // 00:01 -> 60
  // :01 -> 60
  // 01:01 -> 3660
  // 1:01:01 -> 3661
  F.pipe(
    Do.Do(P.parser.parser)
      .bind(
        'hours',
        F.pipe(
          parseTimePart,
          P.parser.map(x => x * 60 * 60),
        ),
      )
      .do(P.char.char(':'))
      .bind(
        'minutes',
        F.pipe(
          parseTimePart,
          P.parser.map(x => x * 60),
        ),
      )
      .do(P.char.char(':'))
      .bind('seconds', parseTimePart)
      .return(({ hours, minutes, seconds }) => hours + minutes + seconds),
    P.parser.alt(() =>
      Do.Do(P.parser.parser)
        .bind(
          'hours',
          F.pipe(
            parseTimePart,
            P.parser.optional,
            P.parser.map(
              O.fold(
                () => 0,
                x => x * 60 * 60,
              ),
            ),
          ),
        )
        .do(P.char.char(':'))
        .bind(
          'minutes',
          F.pipe(
            parseTimePart,
            P.parser.map(x => x * 60),
          ),
        )
        .return(({ hours, minutes }) => hours + minutes),
    ),
    P.parser.alt(() =>
      Do.Do(P.parser.parser)
        .bind(
          'hours',
          F.pipe(
            parseTimePart,
            P.parser.map(x => x * 60 * 60),
          ),
        )
        .do(P.char.char(':'))
        .return(({ hours }) => hours),
    ),
  );

const parseTimeParser: P.parser.Parser<string, number> = Do.Do(P.parser.parser)
  .do(P.parser.many(P.char.space))
  .bind(
    'items',
    P.parser.sepBy1(
      P.parser.many1(P.char.space),
      F.pipe(
        P.parser.many1(
          F.pipe(
            parseSuffixedHour,
            P.parser.alt(() => parseSuffixedMinute),
          ),
        ),
        P.parser.map(sum),
        P.parser.alt(() => parseSuffixedHour),
        P.parser.alt(() => parseSuffixedMinute),
        P.parser.alt(() => parseTimeFromString),
        P.parser.alt(() => parseNumericalHours),
        P.parser.alt(() => parseNumericalMinutes),
      ),
    ),
  )
  .do(P.parser.many(P.char.space))
  .do(P.parser.eof())
  .return(F.flow(({ items }) => items, sum));

// Parse time from various formats into seconds.
// .1 -> 360
// 1 -> 3600
// 1.1 -> 3960
// 0.1 -> 360
// 1h -> 3600
// 1.1h -> 3960
// 0.1h -> 360
// 1m -> 60
// 1.5m -> 90
// 0.5m -> 30
// 1: -> 3600
// 00:01 -> 60
// :01 -> 60
// 01:01 -> 3660
// 1:01:01 -> 3661
export const parseTime: R.Reader<string, E.Either<string, number>> = F.pipe(
  R.ask<string>(),
  R.map(source => run(parseTimeParser, source)),
);
